import React, { useState } from "react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import Reveal from "./Reveal";

const steps = [
  {
    n: "01",
    title: "Assess",
    body: "Understand the facility, workflows, constraints, and goals.",
  },
  {
    n: "02",
    title: "Design",
    body: "Develop the automation strategy and select the right technology.",
  },
  {
    n: "03",
    title: "Test",
    body: "Validate the system and workflows in the MAG Lab before deployment.",
  },
  {
    n: "04",
    title: "Integrate",
    body: "Connect the fleet to the plant systems already in place: WMS, MES, PLCs, and fleet management.",
  },
  {
    n: "05",
    title: "Launch",
    body: "Commission and stabilize the system on the plant floor.",
  },
  {
    n: "06",
    title: "Support",
    body: "Monitor, troubleshoot, and optimize the system over time.",
  },
];

function canHover() {
  return typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
}

export default function HomeProblem() {
  const reduceMotion = usePrefersReducedMotion();
  const [active, setActive] = useState(null);

  return (
    <section
      id="how-we-work"
      className="scroll-mt-24 border-t border-gray-200 bg-white py-12 sm:py-14"
      aria-labelledby="problem-heading"
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-10">
            <div className="max-w-xl">
              <h2
                id="problem-heading"
                className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl"
              >
                One partner from first study to support.
              </h2>
              <p className="mt-3 text-base leading-relaxed text-gray-600">
                One engagement, with no handoff between phases.
              </p>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-gray-500 sm:text-right">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                Six phases
              </span>
              <span className="mt-2 block">
                Assess, Design, Test, Integrate, Launch, Support.
              </span>
            </p>
          </div>

          <ol
            className="mt-6 flex h-40 snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:h-44 lg:overflow-visible lg:pb-0 [&::-webkit-scrollbar]:hidden"
            onMouseLeave={() => {
              if (canHover()) setActive(null);
            }}
          >
            {steps.map((step, index) => {
              const open = active === index;
              const nextTitle = steps[index + 1]?.title;
              return (
                <li
                  key={step.n}
                  className={`group relative h-full w-[min(78vw,16rem)] shrink-0 snap-start overflow-hidden bg-gray-100 text-gray-900 transition-[flex,background-color,color] ease-out lg:w-auto lg:min-w-0 lg:flex-1 ${
                    reduceMotion ? "duration-0" : "duration-300"
                  } ${open ? "bg-gray-900 text-white lg:flex-[1.7]" : ""}`}
                  onMouseEnter={() => {
                    if (canHover()) setActive(index);
                  }}
                >
                  <button
                    type="button"
                    className="flex h-full w-full flex-col p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gray-900 sm:p-5"
                    aria-expanded={open}
                    aria-describedby={`step-${step.n}-body`}
                    onClick={() => {
                      if (canHover()) return;
                      setActive((current) => (current === index ? null : index));
                    }}
                    onFocus={() => setActive(index)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-lg font-semibold tracking-tight sm:text-xl">{step.title}</h3>
                      <span
                        className={`shrink-0 pt-0.5 text-[13px] font-medium tabular-nums ${
                          open ? "text-white/65" : "text-gray-500"
                        }`}
                      >
                        {step.n}
                      </span>
                    </div>
                    <p
                      id={`step-${step.n}-body`}
                      className={`overflow-hidden text-sm leading-relaxed ${
                        reduceMotion ? "" : "transition-[opacity,max-height,margin] duration-300 ease-out"
                      } ${
                        open
                          ? "mt-2 max-h-20 opacity-100 text-white/80"
                          : "mt-0 max-h-0 opacity-0 text-gray-600"
                      }`}
                    >
                      {step.body}
                    </p>
                    <span
                      className={`mt-auto pt-3 text-[11px] font-medium tracking-wide ${
                        open ? "text-white/45" : "text-gray-400"
                      }`}
                    >
                      {nextTitle ? `Then ${nextTitle}` : "Then ongoing"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </Reveal>
      </div>
    </section>
  );
}
